/* ============================================================
   White-label runtime configuration

   Audit A-02: `BRANDS[brandKey]` with a key taken straight from a URL
   parameter reaches inherited Object properties — `?brand=constructor`
   returns a truthy function, walks past the `|| BRANDS.default` guard,
   and was then persisted to localStorage. Lookups now go through an
   own-property check, and only known keys are ever stored.
   ============================================================ */

import { session } from './storage.js';

/* `demonstration: true` marks a partner skin that exists to be SHOWN,
   not one that has been agreed.

   KIFWA and KPA are real organisations — the Kenya International
   Freight & Warehousing Association and the Kenya Ports Authority. Their
   entries here were built to demonstrate what a white-label deployment
   would look like, and they are convincing: real name, real colours, a
   plausible contact address. Anyone reaching /?brand=kifwa got the whole
   product under that association's identity with nothing anywhere on the
   page indicating the relationship is hypothetical — and the entire
   point of a demonstration skin is that the link gets sent to someone.

   The flag drives a visible line in the footer. It is on the brand
   rather than in a page template so a new hypothetical partner cannot be
   added without deciding, in the same object, whether it is real.

   ---- Why no brand carries a contact address ----

   Each of these entries used to have a contactEmail, and nothing had
   ever read one: every address on the site comes from SITE.email. Three
   fields that looked load-bearing and were not.

   Deleted rather than wired up, because wiring them up is the mistake
   they invite. Two of the three were plausible addresses invented for
   real organisations — diagnostics@kifwa.or.ke, cargo@kpa.co.ke — on
   entries the comment above describes as hypothetical. A future reader
   finding an unused field naturally reads it as an oversight and
   connects it, and the result is a page telling visitors to write to an
   association about a product it has not agreed to, at an address
   nobody there has ever seen.

   So the contact stays Kanda's under every skin, which is also the
   truthful answer: Kanda operates the tool and Kanda holds whatever a
   data-protection request would be about, whatever colours the page is
   wearing. scripts/smoke.mjs fails if a brand grows a contact field
   again. */
const BRANDS = Object.freeze({
  default: {
    key: 'default',
    name: 'Kanda',
    shortName: 'Kanda',
    descriptor: 'Logistics Advisory',
    tagline: 'The belt that moves Africa',
    primaryColor: '#1b1f3b',
    primaryColorDark: '#f5f0e8',
    accentColor: '#c75c2e',
    accentColorDark: '#dd7a4f',
    accentTextColor: '#a8481e',
    accentTextColorDark: '#e2825a',
    poweredBy: false
  },

  kifwa: {
    key: 'kifwa',
    name: 'KIFWA Logistics Diagnostic',
    shortName: 'KIFWA',
    descriptor: 'Logistics Diagnostic',
    tagline: 'Kenya International Freight & Warehousing Association',
    primaryColor: '#00443a',
    primaryColorDark: '#eaf4f0',
    accentColor: '#b56a1c',
    accentColorDark: '#e8a355',
    accentTextColor: '#925410',
    accentTextColorDark: '#e8a355',
    poweredBy: true,
    demonstration: true
  },

  kpa: {
    key: 'kpa',
    name: 'KPA Cargo Health Check',
    shortName: 'KPA',
    descriptor: 'Cargo Health Check',
    tagline: 'Kenya Ports Authority Cargo Community',
    primaryColor: '#0d3f7a',
    primaryColorDark: '#e8f1fb',
    accentColor: '#0b7d89',
    accentColorDark: '#45c3cf',
    accentTextColor: '#096169',
    accentTextColorDark: '#45c3cf',
    poweredBy: true,
    demonstration: true
  }
});

const STORAGE_KEY = 'kanda_brand';
let currentBrand = null;

function isKnownBrand(key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(BRANDS, key);
}

export function getBrand() {
  if (currentBrand) return currentBrand;

  let urlBrand = null;
  try {
    urlBrand = new URLSearchParams(window.location.search).get('brand');
  } catch {
    /* malformed query string — fall through to stored/default */
  }

  const storedBrand = session.get(STORAGE_KEY);

  const key = isKnownBrand(urlBrand)
    ? urlBrand
    : isKnownBrand(storedBrand)
      ? storedBrand
      : 'default';

  currentBrand = BRANDS[key];

  // Only persist a key we have actually validated.
  if (isKnownBrand(urlBrand) && urlBrand !== storedBrand) {
    session.set(STORAGE_KEY, urlBrand);
  }

  return currentBrand;
}

export function setBrand(brandKey) {
  if (!isKnownBrand(brandKey)) return false;
  session.set(STORAGE_KEY, brandKey);
  currentBrand = BRANDS[brandKey];
  applyBrand();
  return true;
}

export function applyBrand() {
  const brand = getBrand();
  const root = document.documentElement;

  // Set the two variants, not the resolved token. Writing --brand-primary
  // directly here would be an inline style on :root, which outranks the
  // prefers-color-scheme block and pinned the wordmark to its light-mode
  // colour — navy text on a navy surface in dark mode. The stylesheet
  // picks between these per scheme instead.
  root.style.setProperty('--brand-primary-light', brand.primaryColor);
  root.style.setProperty('--brand-primary-dark', brand.primaryColorDark || brand.primaryColor);
  root.style.setProperty('--brand-accent-light', brand.accentColor);
  root.style.setProperty('--brand-accent-dark', brand.accentColorDark || brand.accentColor);
  /* A partner's accent is their colour and is set unchanged above. This
     is the darkened sibling used only where that colour becomes type —
     KIFWA's amber is 3.67:1 on Warm Sand as a word, fine as a fill.
     Falls back to the accent itself so a brand added without one is
     visibly wrong in the sweep rather than silently unreadable. */
  root.style.setProperty('--brand-accent-text-light', brand.accentTextColor || brand.accentColor);
  root.style.setProperty(
    '--brand-accent-text-dark',
    brand.accentTextColorDark || brand.accentColorDark || brand.accentColor
  );
  root.dataset.brand = brand.key;

  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) metaTheme.setAttribute('content', brand.primaryColor);

  // Audit A-13: the previous implementation set document.title here on
  // every apply, clobbering per-page titles. Titles are the router's job.
  window.dispatchEvent(new CustomEvent('brandchange', { detail: brand }));
}

export function getBrandList() {
  return Object.values(BRANDS).map((b) => ({ key: b.key, name: b.name }));
}

export function isWhiteLabel() {
  return getBrand().key !== 'default';
}

/**
 * Is the active skin a demonstration rather than an agreed partnership?
 * Defaults to TRUE for any non-default brand that does not say
 * otherwise: a skin added without anyone deciding is a skin nobody has
 * confirmed is real, and the safe reading of that is "not yet".
 */
export function isDemonstration() {
  const brand = getBrand();
  if (brand.key === 'default') return false;
  return brand.demonstration !== false;
}
